import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Telescope, Image as ImageIcon, Check, X, Edit3, ExternalLink, Sparkles, AlertCircle, FileText, Beaker, Leaf, Cog, Quote, BookText, Wine, Award, History, PenLine } from "lucide-react";
import {
  enrichSummary, enrichScout, enrichActSuggestion,
  enrichCreateImage, enrichActImage, enrichRenormalizeImage,
  enrichFlavourScout, enrichActFlavour, enrichSetPrimarySource,
  enrichEditorialRewrite, enrichActEditorial, enrichWhyMatters,
} from "../../lib/adminApi";

const TABS = [
  { key: "images", label: "Images", icon: ImageIcon, fields: [] },
  { key: "flavour_map", label: "Flavour Map", icon: Beaker, fields: ["flavour_map"] },
  { key: "flavour", label: "Flavour notes", icon: Beaker, fields: ["flavour_nose", "flavour_palate", "flavour_finish"] },
  { key: "botanicals", label: "Botanicals", icon: Leaf, fields: ["botanicals"] },
  { key: "production", label: "Production", icon: Cog, fields: ["production_still", "production_method", "production_water", "production_batch", "distillery", "region", "country", "abv"] },
  { key: "story", label: "Story", icon: BookText, fields: ["brand_story"] },
  { key: "trivia", label: "Bottle Trivia", icon: Quote, fields: ["bottle_trivia"] },
  { key: "serves", label: "Serves", icon: Wine, fields: ["extended_serves"] },
  { key: "awards", label: "Awards", icon: Award, fields: ["awards"] },
  { key: "editorial", label: "Editorial", icon: PenLine, fields: [] },
  { key: "sources", label: "Sources", icon: History, fields: [] },
];

const FIELD_LABELS = {
  abv: "ABV", distillery: "Distillery", region: "Region", country: "Country",
  botanicals: "Botanicals", production_still: "Still",
  production_method: "Distillation method", production_water: "Water source",
  production_batch: "Batch type", awards: "Awards",
  brand_story: "Brand story", production_notes: "How it's made",
  bottle_trivia: "Why this bottle matters",
  flavour_nose: "Nose", flavour_palate: "Palate", flavour_finish: "Finish",
  extended_serves: "Recommended serves & garnish",
};

const CONFIDENCE_TONE = {
  VERIFIED: "text-green-400 border-green-700/40",
  STRONG: "text-[#C17F59] border-[#C17F59]/40",
  PARTIAL: "text-amber-400 border-amber-600/40",
  NEEDS_REVIEW: "text-red-400 border-red-600/40",
};

export default function AdminEnrichment() {
  const { id } = useParams();
  const [summary, setSummary] = useState(null);
  const [activeTab, setActiveTab] = useState("images");
  const [scoutUrl, setScoutUrl] = useState("");
  const [scoutNotes, setScoutNotes] = useState("");
  const [scouting, setScouting] = useState(false);
  const [scoutMsg, setScoutMsg] = useState(null);
  const [imgUrl, setImgUrl] = useState("");
  const [imgSourcePage, setImgSourcePage] = useState("");
  const [imgBusy, setImgBusy] = useState(false);

  const load = () => enrichSummary(id).then(setSummary).catch(() => setSummary(null));
  useEffect(() => { load(); }, [id]);

  const runScout = async () => {
    if (!scoutUrl.trim()) return;
    setScouting(true); setScoutMsg(null);
    try {
      const r = await enrichScout({ gin_id: id, source_url: scoutUrl.trim(), notes: scoutNotes });
      setScoutMsg({ ok: true, text: r.skipped_reason || `Found ${r.created} suggestions across ${r.fields?.length || 0} fields (${r.source_type}).` });
      setScoutUrl(""); setScoutNotes("");
      load();
    } catch (e) {
      setScoutMsg({ ok: false, text: e.response?.data?.detail || "Scout failed" });
    } finally { setScouting(false); }
  };

  const submitImage = async () => {
    if (!imgUrl.trim()) return;
    setImgBusy(true);
    try {
      await enrichCreateImage({
        gin_id: id, image_url: imgUrl.trim(),
        source_page_url: imgSourcePage.trim() || null,
        license_status: "needs_review",
      });
      setImgUrl(""); setImgSourcePage("");
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Image fetch failed");
    } finally { setImgBusy(false); }
  };

  const act = async (sid, action, edited, extra = {}) => {
    try {
      await enrichActSuggestion(sid, { action, admin_edited_value: edited, ...extra });
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Action failed");
    }
  };

  const actImage = async (cid, action) => {
    try {
      await enrichActImage(cid, { action });
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Action failed");
    }
  };

  if (!summary) return <div className="p-10 text-white/40">Pulling the research file...</div>;

  const completenessPct = Math.round((summary.completeness || 0) * 100);
  const tabFields = (TABS.find((t) => t.key === activeTab) || {}).fields || [];
  const tabPending = (summary.pending_suggestions || []).filter((s) => tabFields.includes(s.field_type));
  const tabApproved = (summary.approved_suggestions || []).filter((s) => tabFields.includes(s.field_type));
  const tabRejected = (summary.rejected_suggestions || []).filter((s) => tabFields.includes(s.field_type));

  return (
    <div className="px-5 md:px-10 py-8 max-w-7xl" data-testid={`admin-enrichment-${id}`}>
      <Link to={`/admin/menu/${id}`} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/50 hover:text-[#C17F59]"
            data-testid="back-to-editor">
        <ArrowLeft size={12} /> Gin Editor
      </Link>

      {/* Editorial header */}
      <header className="mt-6 mb-7 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="overline text-[#C17F59] mb-2 flex items-center gap-2"><Telescope size={12} /> Enrichment Scout · {id}</p>
          <h1 className="font-display text-4xl md:text-5xl tracking-tight">The research file.</h1>
          <p className="font-hand text-xl text-[#D89A74] -rotate-1 mt-2">— sources in, story out.</p>
        </div>
        <div className="text-right shrink-0">
          <p className="overline text-white/40">Atlas completeness</p>
          <p className="font-display text-5xl text-[#C17F59] tabular-nums leading-none" data-testid="completeness-pct">{completenessPct}<span className="text-xl">%</span></p>
          <div className="h-px w-32 bg-white/10 mt-2 overflow-hidden">
            <div className="h-full bg-[#C17F59]" style={{ width: `${completenessPct}%` }} />
          </div>
        </div>
      </header>

      {/* Scout intake */}
      <section className="bg-[#101113] border border-[#C17F59]/30 p-6 mb-8" data-testid="scout-intake">
        <p className="overline text-[#C17F59] mb-3 flex items-center gap-2"><Sparkles size={12} /> Bring in a source</p>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          <div className="md:col-span-7">
            <label className="block">
              <span className="overline text-white/50 mb-1 block">Source URL</span>
              <input value={scoutUrl} onChange={(e) => setScoutUrl(e.target.value)}
                     placeholder="Paste official brand page, Wikipedia, press release..."
                     className="w-full bg-transparent border-b border-white/15 focus:border-[#C17F59] py-2 text-sm focus:outline-none transition-colors"
                     data-testid="scout-url" />
            </label>
          </div>
          <div className="md:col-span-5">
            <label className="block">
              <span className="overline text-white/50 mb-1 block">Notes (optional)</span>
              <input value={scoutNotes} onChange={(e) => setScoutNotes(e.target.value)}
                     placeholder="e.g. official press kit, distillery FAQ"
                     className="w-full bg-transparent border-b border-white/15 focus:border-[#C17F59] py-2 text-sm focus:outline-none"
                     data-testid="scout-notes" />
            </label>
          </div>
        </div>
        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <button onClick={runScout} disabled={scouting || !scoutUrl.trim()}
                  className="inline-flex items-center gap-2 bg-[#C17F59] text-[#0E0F0C] px-5 py-2.5 text-xs uppercase tracking-[0.25em] hover:bg-[#D89A74] disabled:opacity-50"
                  data-testid="scout-run">
            <Telescope size={12} /> {scouting ? "Reading the source..." : "Read source & extract"}
          </button>
          {scoutMsg && (
            <p className={`text-xs ${scoutMsg.ok ? "text-green-400" : "text-red-400"}`} data-testid="scout-message">
              {scoutMsg.text}
            </p>
          )}
        </div>
        <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-3">
          Nothing publishes automatically. Every fact lands in the queue below for your approval.
        </p>
      </section>

      {/* Sub-tabs */}
      <nav className="flex gap-1 mb-5 border-b border-white/8 overflow-x-auto no-scrollbar" data-testid="enrich-tabs">
        {TABS.map((t) => {
          const pendingCount = t.fields.length === 0
            ? (t.key === "images" ? summary.pending_image_candidates.length :
               t.key === "sources" ? summary.history.length : 0)
            : (summary.pending_suggestions || []).filter((s) => t.fields.includes(s.field_type)).length;
          const active = activeTab === t.key;
          return (
            <button key={t.key} onClick={() => setActiveTab(t.key)}
                    className={`flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] px-3 py-2 transition-colors shrink-0 ${
                      active ? "text-[#C17F59] border-b border-[#C17F59]" : "text-white/50 hover:text-white"
                    }`}
                    data-testid={`enrich-tab-${t.key}`}>
              <t.icon size={12} /> {t.label}
              {pendingCount > 0 && <span className="ml-1 text-[10px] text-[#D89A74]">({pendingCount})</span>}
            </button>
          );
        })}
      </nav>

      {/* IMAGES TAB */}
      {activeTab === "images" && (
        <div className="space-y-6" data-testid="tab-images">
          {/* Current */}
          <SectionCard label="Current bottle">
            <div className="flex items-start gap-5">
              <div className="w-24 h-32 border border-white/10 flex items-center justify-center bg-[#101113] shrink-0">
                {summary.current.image_url ? (
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${summary.current.image_url}`} alt="" className="max-w-full max-h-full object-contain" />
                ) : <span className="text-[10px] text-white/30 uppercase tracking-[0.2em]">Placeholder</span>}
              </div>
              <div className="text-sm text-white/65">
                <p className="overline text-white/40">State</p>
                <p>{summary.current.image_state || "placeholder"}</p>
              </div>
            </div>
          </SectionCard>

          {/* New candidate intake */}
          <SectionCard label="Bring in an image candidate">
            <p className="text-xs text-white/55 mb-3">
              Paste a bottle image URL (PNG/WebP transparent cutout preferred). We download a local copy — the public Atlas never hotlinks.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="block">
                <span className="overline text-white/50 mb-1 block">Image URL</span>
                <input value={imgUrl} onChange={(e) => setImgUrl(e.target.value)}
                       placeholder="https://brand.com/bottles/hapusa.png"
                       className="w-full bg-transparent border-b border-white/15 focus:border-[#C17F59] py-2 text-sm focus:outline-none"
                       data-testid="img-candidate-url" />
              </label>
              <label className="block">
                <span className="overline text-white/50 mb-1 block">Source page (optional)</span>
                <input value={imgSourcePage} onChange={(e) => setImgSourcePage(e.target.value)}
                       placeholder="https://brand.com/press"
                       className="w-full bg-transparent border-b border-white/15 focus:border-[#C17F59] py-2 text-sm focus:outline-none"
                       data-testid="img-candidate-page" />
              </label>
            </div>
            <button onClick={submitImage} disabled={imgBusy || !imgUrl.trim()}
                    className="mt-4 inline-flex items-center gap-2 bg-[#C17F59] text-[#0E0F0C] px-5 py-2 text-xs uppercase tracking-[0.25em] hover:bg-[#D89A74] disabled:opacity-50"
                    data-testid="img-candidate-submit">
              <ImageIcon size={12} /> {imgBusy ? "Downloading..." : "Download & queue"}
            </button>
          </SectionCard>

          {/* Pending images */}
          {summary.pending_image_candidates.length > 0 && (
            <SectionCard label={`Pending image candidates · ${summary.pending_image_candidates.length}`}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {summary.pending_image_candidates.map((c) => (
                  <CandidatePreview key={c.id} c={c} onAct={actImage} onRenorm={async (cid) => {
                    try { await enrichRenormalizeImage(cid); load(); } catch (e) { alert(e.response?.data?.detail || "Renormalize failed"); }
                  }} />
                ))}
              </div>
            </SectionCard>
          )}

          {/* Already-approved candidates (so admin can renormalize a tuned bottle later) */}
          {summary.approved_image_candidates.length > 0 && (
            <SectionCard label={`Approved · ${summary.approved_image_candidates.length}`}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {summary.approved_image_candidates.slice(0, 4).map((c) => (
                  <CandidatePreview key={c.id} c={c} approved onAct={() => {}} onRenorm={async (cid) => {
                    try { await enrichRenormalizeImage(cid); load(); } catch (e) { alert(e.response?.data?.detail || "Renormalize failed"); }
                  }} />
                ))}
              </div>
            </SectionCard>
          )}
        </div>
      )}

      {/* SOURCES (audit timeline) */}
      {activeTab === "sources" && (
        <div className="space-y-4" data-testid="tab-sources">
          <SectionCard label={`Source audit timeline · ${summary.history.length} events`}>
            {summary.history.length === 0 && <p className="text-white/40 text-sm">No enrichment events yet for this gin.</p>}
            <ol className="space-y-3">
              {summary.history.map((h, i) => (
                <li key={i} className="border-l-2 border-[#C17F59]/40 pl-4 py-1" data-testid={`history-${i}`}>
                  <p className="overline text-[#C17F59]">{h.event_type.replace(/_/g, " ")}</p>
                  <p className="text-sm text-white/75">{h.admin_email || "system"} · {new Date(h.timestamp).toLocaleString()}</p>
                  {h.payload?.source_url && (
                    <a href={h.payload.source_url} target="_blank" rel="noreferrer"
                       className="text-xs text-[#D89A74] hover:underline inline-flex items-center gap-1 mt-1">
                      {h.payload.source_url.slice(0, 80)} <ExternalLink size={10} />
                    </a>
                  )}
                  {h.payload?.fields && (
                    <p className="text-xs text-white/55 mt-1">Fields: {h.payload.fields.join(", ")}</p>
                  )}
                  {h.payload?.final_value && (
                    <p className="text-xs text-white/55 mt-1 italic line-clamp-2">"{String(h.payload.final_value).slice(0, 200)}"</p>
                  )}
                </li>
              ))}
            </ol>
          </SectionCard>
        </div>
      )}

      {/* EDITORIAL TAB */}
      {activeTab === "editorial" && (
        <EditorialTab id={id} summary={summary} reload={load} />
      )}

      {/* FLAVOUR MAP TAB */}
      {activeTab === "flavour_map" && (
        <FlavourMapTab id={id} summary={summary} reload={load} />
      )}

      {/* FIELD TABS (flavour / botanicals / production / story / trivia / serves / awards) */}
      {tabFields.length > 0 && (
        <div className="space-y-6" data-testid={`tab-${activeTab}`}>
          {/* Current */}
          <SectionCard label="Currently approved">
            <div className="space-y-3">
              {tabFields.map((f) => {
                const key = ({
                  brand_story: "story", bottle_trivia: "trivia",
                  flavour_nose: "nose", flavour_palate: "palate", flavour_finish: "finish",
                }[f]) || f;
                const val = summary.current[key];
                return (
                  <div key={f} className="grid grid-cols-1 md:grid-cols-4 gap-3 py-2 border-b border-white/5 last:border-0">
                    <p className="overline text-white/40">{FIELD_LABELS[f] || f}</p>
                    <div className="md:col-span-3 text-sm text-white/80">
                      {val ? (Array.isArray(val) ? val.join(", ") : (typeof val === "object" ? JSON.stringify(val) : String(val))) : <span className="text-white/30 italic">— empty</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </SectionCard>

          {/* Pending */}
          {tabPending.length > 0 && (
            <SectionCard label={`Pending suggestions · ${tabPending.length}`}>
              <div className="space-y-3">
                {tabPending.map((s) => <SuggestionCard key={s.id} s={s} onAct={act} />)}
              </div>
            </SectionCard>
          )}

          {/* Approved history */}
          {tabApproved.length > 0 && (
            <SectionCard label={`Approved · ${tabApproved.length}`}>
              <div className="space-y-2">
                {tabApproved.slice(0, 5).map((s) => (
                  <div key={s.id} className="text-xs text-white/55 border-l-2 border-green-700/40 pl-3 py-1" data-testid={`approved-${s.id}`}>
                    <p className="overline text-green-400">{FIELD_LABELS[s.field_type]} · approved by {s.approved_by}</p>
                    <p className="italic mt-1 line-clamp-2">"{Array.isArray(s.final_value) ? s.final_value.join(', ') : String(s.final_value || '').slice(0, 200)}"</p>
                    <a href={s.source_url} target="_blank" rel="noreferrer" className="text-[#D89A74] inline-flex items-center gap-1 mt-1">
                      {s.source_domain} <ExternalLink size={10} />
                    </a>
                  </div>
                ))}
              </div>
            </SectionCard>
          )}

          {/* Rejected (compact) */}
          {tabRejected.length > 0 && (
            <SectionCard label={`Rejected · ${tabRejected.length}`}>
              <div className="space-y-1 text-xs text-white/40">
                {tabRejected.slice(0, 5).map((s) => (
                  <p key={s.id} className="line-clamp-1">✗ {FIELD_LABELS[s.field_type]} from {s.source_domain} — {s.rejection_reason || "no reason"}</p>
                ))}
              </div>
            </SectionCard>
          )}

          {tabPending.length === 0 && tabApproved.length === 0 && (
            <div className="text-center py-10 text-white/40">
              <FileText size={20} className="mx-auto opacity-40 mb-2" />
              <p className="text-sm">No suggestions yet for this section. Paste a source URL above to scout.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SectionCard({ label, children }) {
  return (
    <section className="bg-[#15171A] border border-white/8 p-5">
      <p className="overline text-[#C17F59] mb-3">{label}</p>
      {children}
    </section>
  );
}

function EditorialTab({ id, summary, reload }) {
  const [busy, setBusy] = React.useState(null); // field name being worked on
  const [msg, setMsg] = React.useState(null);

  const cur = summary.current || {};
  const editorialFields = [
    { key: "story", label: "The Story", value: cur.story },
    { key: "production_notes", label: "How It's Made", value: cur.production_notes },
    { key: "trivia", label: "Why This Bottle Matters", value: cur.trivia },
    { key: "nose", label: "Nose", value: cur.nose },
    { key: "palate", label: "Palate", value: cur.palate },
    { key: "finish", label: "Finish", value: cur.finish },
    { key: "extended_serves", label: "Recommended serves", value: cur.extended_serves },
  ];

  const pending = (summary.pending_suggestions || []).filter((s) => s.category === "editorial_rewrite");
  const approved = (summary.approved_suggestions || []).filter((s) => s.category === "editorial_rewrite");

  const runRewrite = async (field) => {
    setBusy(field); setMsg(null);
    try {
      await enrichEditorialRewrite({ gin_id: id, field });
      setMsg({ ok: true, text: `BENO-voice rewrite drafted for ${field}.` });
      reload();
    } catch (e) {
      setMsg({ ok: false, text: e.response?.data?.detail || "Rewrite failed" });
    } finally { setBusy(null); }
  };

  const runEssay = async () => {
    setBusy("essay"); setMsg(null);
    try {
      const r = await enrichWhyMatters({ gin_id: id });
      setMsg({ ok: true, text: `Essay drafted (${r.length} chars).` });
      reload();
    } catch (e) {
      setMsg({ ok: false, text: e.response?.data?.detail || "Essay failed" });
    } finally { setBusy(null); }
  };

  const act = async (sid, action, edited) => {
    try {
      await enrichActEditorial(sid, { action, admin_edited_value: edited });
      reload();
    } catch (e) { alert(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="tab-editorial">
      <SectionCard label="BENO-voice editorial layer">
        <p className="text-sm text-white/55 mb-4">
          Take any approved fact and let the BENO editor rewrite it in the bar's voice. Generate a
          "Why It Matters" essay from the full enrichment file. Every rewrite lands as a pending
          suggestion — nothing replaces approved copy without your sign-off.
        </p>

        {/* Per-field rewrite buttons */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
          {editorialFields.map((f) => (
            <div key={f.key} className={`border p-3 ${f.value ? "border-white/10" : "border-white/5 opacity-50"}`} data-testid={`editorial-row-${f.key}`}>
              <p className="overline text-white/40 mb-1">{f.label}</p>
              <p className="text-xs text-white/65 italic mb-3 line-clamp-2">
                {f.value || "— no approved copy yet —"}
              </p>
              <button onClick={() => runRewrite(f.key)} disabled={!f.value || busy === f.key}
                      className="inline-flex items-center gap-1 border border-[#C17F59]/40 text-[#C17F59] hover:bg-[#C17F59] hover:text-[#0E0F0C] px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] disabled:opacity-40"
                      data-testid={`rewrite-${f.key}`}>
                <PenLine size={11} /> {busy === f.key ? "Drafting..." : "BENO voice rewrite"}
              </button>
            </div>
          ))}
        </div>

        {/* Why it matters essay */}
        <div className="border border-[#C17F59]/30 bg-[#0E0F0C] p-4">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
            <p className="overline text-[#C17F59]">Why It Matters · 180-280 word essay</p>
            <button onClick={runEssay} disabled={busy === "essay"}
                    className="inline-flex items-center gap-2 bg-[#C17F59] text-[#0E0F0C] px-4 py-2 text-xs uppercase tracking-[0.22em] hover:bg-[#D89A74] disabled:opacity-50"
                    data-testid="essay-generate">
              <BookText size={11} /> {busy === "essay" ? "Drafting..." : "Draft essay"}
            </button>
          </div>
          {cur.why_it_matters && (
            <p className="text-xs text-white/65 italic mt-2 line-clamp-3">{cur.why_it_matters}</p>
          )}
          <p className="text-[10px] text-white/40 mt-2">
            Knits story · production · botanicals · awards into one arc.
            Requires at least one approved enrichment field on this gin.
          </p>
        </div>

        {msg && <p className={`mt-4 text-xs ${msg.ok ? "text-green-400" : "text-red-400"}`} data-testid="editorial-msg">{msg.text}</p>}
      </SectionCard>

      {/* Pending editorial suggestions */}
      {pending.length > 0 && (
        <SectionCard label={`Pending editorial drafts · ${pending.length}`}>
          <div className="space-y-3">
            {pending.map((s) => <EditorialDraftCard key={s.id} s={s} onAct={act} />)}
          </div>
        </SectionCard>
      )}

      {/* Approved (compact) */}
      {approved.length > 0 && (
        <SectionCard label={`Approved · ${approved.length}`}>
          <div className="space-y-2">
            {approved.slice(0, 5).map((s) => (
              <div key={s.id} className="border-l-2 border-green-700/40 pl-3 py-1 text-xs">
                <p className="overline text-green-400">{s.rewrite_target_field} · approved by {s.approved_by}</p>
                <p className="text-white/65 italic line-clamp-2 mt-1">"{String(s.final_value || s.suggested_value || '').slice(0, 220)}"</p>
              </div>
            ))}
          </div>
        </SectionCard>
      )}
    </div>
  );
}

function EditorialDraftCard({ s, onAct }) {
  const [editing, setEditing] = React.useState(false);
  const [val, setVal] = React.useState(String(s.suggested_value || ""));
  const target = s.rewrite_target_field;
  const isEssay = target === "why_it_matters";

  return (
    <div className="bg-[#101113] border border-white/8 p-4" data-testid={`editorial-draft-${s.id}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <p className="overline text-[#C17F59]">{target} · BENO editor draft</p>
        <ConfidenceChip state={s.confidence_state} />
      </div>

      <div className={`grid grid-cols-1 ${isEssay ? "" : "md:grid-cols-2"} gap-4 mb-3`}>
        <div>
          <p className="overline text-white/40 mb-1">Currently</p>
          <p className="text-xs text-white/55 italic">
            {s.current_value ? String(s.current_value).slice(0, 380) : <span className="text-white/30">— empty —</span>}
          </p>
        </div>
        <div>
          <p className="overline text-[#C17F59] mb-1">BENO voice draft</p>
          {editing ? (
            <textarea value={val} onChange={(e) => setVal(e.target.value)}
                      rows={isEssay ? 12 : 4}
                      className="w-full bg-transparent border border-[#C17F59]/40 focus:border-[#C17F59] p-2 text-sm focus:outline-none resize-y"
                      data-testid={`editorial-edit-${s.id}`} />
          ) : (
            <p className="text-sm text-white/85 whitespace-pre-wrap" data-testid={`editorial-text-${s.id}`}>
              {s.suggested_value}
            </p>
          )}
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        {!editing ? (
          <>
            <button onClick={() => onAct(s.id, "approve")}
                    className="inline-flex items-center gap-1 bg-[#C17F59] text-[#0E0F0C] px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] hover:bg-[#D89A74]"
                    data-testid={`editorial-approve-${s.id}`}>
              <Check size={11} /> Publish
            </button>
            <button onClick={() => setEditing(true)}
                    className="inline-flex items-center gap-1 border border-white/15 px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] text-white/70 hover:text-[#C17F59] hover:border-[#C17F59]"
                    data-testid={`editorial-edit-button-${s.id}`}>
              <Edit3 size={11} /> Edit first
            </button>
            <button onClick={() => onAct(s.id, "reject")}
                    className="inline-flex items-center gap-1 border border-white/15 px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] text-white/60 hover:text-red-400 hover:border-red-400/40"
                    data-testid={`editorial-reject-${s.id}`}>
              <X size={11} /> Reject
            </button>
          </>
        ) : (
          <>
            <button onClick={() => { onAct(s.id, "edit_approve", val); setEditing(false); }}
                    className="inline-flex items-center gap-1 bg-[#C17F59] text-[#0E0F0C] px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] hover:bg-[#D89A74]"
                    data-testid={`editorial-save-${s.id}`}>
              <Check size={11} /> Save & publish
            </button>
            <button onClick={() => setEditing(false)}
                    className="inline-flex items-center gap-1 border border-white/15 px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] text-white/60">
              Cancel
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function FlavourMapTab({ id, summary, reload }) {
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState(null);
  const [primaryUrl, setPrimaryUrl] = React.useState(summary.current?.primary_source_url || "");
  const [savingPrimary, setSavingPrimary] = React.useState(false);

  const pending = (summary.pending_suggestions || []).filter((s) => s.field_type === "flavour_map");
  const approved = (summary.approved_suggestions || []).filter((s) => s.field_type === "flavour_map");

  const runScout = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await enrichFlavourScout({ gin_id: id });
      if (r.created === 0) {
        setMsg({ ok: false, text: r.skipped_reason || "Source did not yield a usable flavour map. Try a richer primary source." });
      } else {
        setMsg({ ok: true, text: `Flavour map scouted from ${r.source?.origin === "wikipedia_fallback" ? "Wikipedia fallback" : r.source?.origin}.` });
      }
      reload();
    } catch (e) {
      setMsg({ ok: false, text: e.response?.data?.detail || "Flavour scout failed" });
    } finally { setBusy(false); }
  };

  const savePrimary = async () => {
    setSavingPrimary(true);
    try {
      await enrichSetPrimarySource(id, { primary_source_url: primaryUrl.trim() || null });
      setMsg({ ok: true, text: "Primary research source saved." });
    } catch (e) { setMsg({ ok: false, text: e.response?.data?.detail || "Failed to save" }); }
    finally { setSavingPrimary(false); }
  };

  const act = async (sid, action, edited) => {
    try {
      await enrichActFlavour(sid, { action, admin_edited_value: edited });
      reload();
    } catch (e) { alert(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="tab-flavour_map">
      {/* Primary source editor */}
      <SectionCard label="Primary research source">
        <div className="flex gap-3 items-end">
          <label className="flex-1">
            <span className="overline text-white/50 mb-1 block">URL (used by Scout & Bulk job; leave blank to fall back to Wikipedia)</span>
            <input value={primaryUrl} onChange={(e) => setPrimaryUrl(e.target.value)}
                   placeholder="https://..."
                   className="w-full bg-transparent border-b border-white/15 focus:border-[#C17F59] py-2 text-sm focus:outline-none"
                   data-testid="primary-source-url" />
          </label>
          <button onClick={savePrimary} disabled={savingPrimary}
                  className="border border-[#C17F59]/40 text-[#C17F59] hover:bg-[#C17F59] hover:text-[#0E0F0C] px-4 py-2 text-[11px] uppercase tracking-[0.22em] disabled:opacity-50"
                  data-testid="primary-source-save">
            {savingPrimary ? "Saving..." : "Save"}
          </button>
        </div>
      </SectionCard>

      <SectionCard label="Run flavour map scout">
        <p className="text-sm text-white/55 mb-3">
          Scout extracts a 10-axis BENO DNA, nose / palate / finish, and per-axis reasoning from
          the gin's primary source (or Wikipedia fallback). Suggestion lands below for approval.
        </p>
        <button onClick={runScout} disabled={busy}
                className="inline-flex items-center gap-2 bg-[#C17F59] text-[#0E0F0C] px-5 py-2.5 text-xs uppercase tracking-[0.25em] hover:bg-[#D89A74] disabled:opacity-50"
                data-testid="flavour-scout-run">
          <Beaker size={12} /> {busy ? "Cartographing..." : "Map the flavour"}
        </button>
        {msg && <p className={`mt-3 text-xs ${msg.ok ? "text-green-400" : "text-red-400"}`} data-testid="flavour-msg">{msg.text}</p>}
      </SectionCard>

      {pending.length > 0 && (
        <SectionCard label={`Pending flavour map · ${pending.length}`}>
          <div className="space-y-4">
            {pending.map((s) => <FlavourSuggestionCard key={s.id} s={s} onAct={act} />)}
          </div>
        </SectionCard>
      )}

      {approved.length > 0 && (
        <SectionCard label={`Approved · ${approved.length}`}>
          {approved.slice(0, 3).map((s) => (
            <div key={s.id} className="border-l-2 border-green-700/40 pl-3 py-2 text-xs text-white/55">
              <p className="overline text-green-400">{s.confidence_state} · approved by {s.approved_by}</p>
              <p className="italic mt-1 line-clamp-2">DNA from {s.source_domain}</p>
            </div>
          ))}
        </SectionCard>
      )}
    </div>
  );
}

function FlavourSuggestionCard({ s, onAct }) {
  const [editing, setEditing] = React.useState(false);
  const sug = s.suggested_value || {};
  const cur = s.current_value || {};
  const [dna, setDna] = React.useState(sug.dna || {});

  const axes = ["juniper", "citrus", "floral", "spice", "herbal", "fruit", "earthy", "coastal", "sweetness", "complexity"];

  const updateAxis = (k, v) => setDna((d) => ({ ...d, [k]: Math.max(0, Math.min(5, parseInt(v || 0, 10))) }));

  return (
    <div className="bg-[#101113] border border-white/8 p-4" data-testid={`flavour-suggestion-${s.id}`}>
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <p className="overline text-[#C17F59]">Source-derived flavour map</p>
        <div className="flex items-center gap-2">
          <ConfidenceChip state={s.confidence_state} />
          <a href={s.source_url} target="_blank" rel="noreferrer" className="text-[10px] uppercase tracking-[0.2em] text-[#D89A74] hover:underline inline-flex items-center gap-1">
            {s.source_domain} · {s.source_origin} <ExternalLink size={10} />
          </a>
        </div>
      </div>

      {/* Nose / palate / finish */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        {["nose", "palate", "finish"].map((k) => (
          <div key={k}>
            <p className="overline text-white/40 mb-1">{k}</p>
            <p className="text-xs text-white/55 line-through line-clamp-2">{cur[k] || "—"}</p>
            <p className="text-sm text-white/85 italic mt-1">{sug[k] || "—"}</p>
          </div>
        ))}
      </div>

      {/* DNA delta */}
      <div className="border border-white/8 p-3 mb-4">
        <p className="overline text-[#C17F59] mb-3">BENO Gin DNA · current → suggested</p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {axes.map((a) => {
            const curV = (cur.dna || {})[a] || 0;
            const sugV = editing ? (dna[a] ?? 0) : ((sug.dna || {})[a] || 0);
            const delta = sugV - curV;
            return (
              <div key={a} className="text-center">
                <p className="overline text-white/40 capitalize">{a}</p>
                <div className="flex items-center justify-center gap-2 mt-1">
                  <span className="text-white/40 text-sm tabular-nums">{curV}</span>
                  <span className={`text-[10px] ${delta > 0 ? "text-green-400" : delta < 0 ? "text-red-400" : "text-white/30"}`}>
                    {delta > 0 ? `+${delta}` : delta < 0 ? delta : "·"}
                  </span>
                  {editing ? (
                    <input type="number" min="0" max="5" value={sugV}
                           onChange={(e) => updateAxis(a, e.target.value)}
                           className="w-10 bg-transparent border-b border-[#C17F59]/40 text-center text-[#C17F59] text-sm tabular-nums" />
                  ) : (
                    <span className="font-display text-xl text-[#C17F59] tabular-nums">{sugV}</span>
                  )}
                </div>
                {sug.dna_reasoning && sug.dna_reasoning[a] && (
                  <p className="text-[9px] text-white/40 italic mt-1 line-clamp-2">{sug.dna_reasoning[a]}</p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Source snippet */}
      {s.source_snippet && (
        <div className="border-l-2 border-[#C17F59]/30 pl-3 mb-4">
          <p className="overline text-white/40 mb-1">Source quote</p>
          <p className="text-[11px] text-white/55 italic line-clamp-3">"{s.source_snippet}"</p>
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        {!editing ? (
          <>
            <button onClick={() => onAct(s.id, "approve")}
                    className="inline-flex items-center gap-1 bg-[#C17F59] text-[#0E0F0C] px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] hover:bg-[#D89A74]"
                    data-testid={`flavour-approve-${s.id}`}>
              <Check size={11} /> Approve DNA
            </button>
            <button onClick={() => setEditing(true)}
                    className="inline-flex items-center gap-1 border border-white/15 px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] text-white/70 hover:text-[#C17F59] hover:border-[#C17F59]"
                    data-testid={`flavour-edit-${s.id}`}>
              <Edit3 size={11} /> Edit DNA
            </button>
            <button onClick={() => onAct(s.id, "reject")}
                    className="inline-flex items-center gap-1 border border-white/15 px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] text-white/60 hover:text-red-400 hover:border-red-400/40"
                    data-testid={`flavour-reject-${s.id}`}>
              <X size={11} /> Keep current
            </button>
          </>
        ) : (
          <>
            <button onClick={() => { onAct(s.id, "edit_approve", { ...sug, dna }); setEditing(false); }}
                    className="inline-flex items-center gap-1 bg-[#C17F59] text-[#0E0F0C] px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] hover:bg-[#D89A74]"
                    data-testid={`flavour-save-${s.id}`}>
              <Check size={11} /> Save & approve
            </button>
            <button onClick={() => setEditing(false)} className="inline-flex items-center gap-1 border border-white/15 px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] text-white/60">
              Cancel
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function CandidatePreview({ c, onAct, onRenorm, approved = false }) {  const norm = c.normalization || {};
  const apiBase = process.env.REACT_APP_BACKEND_URL;
  const orig = c.raw_url ? `${apiBase}${c.raw_url}` : null;
  const final = `${apiBase}${c.local_url}`;
  const [w, h] = norm.original_size || [];
  const [bw, bh] = norm.bbox_size || [];
  return (
    <div className="bg-[#101113] border border-white/8 p-4" data-testid={`img-pending-${c.id}`}>
      <div className="grid grid-cols-3 gap-3 mb-3">
        {/* ORIGINAL */}
        <div>
          <p className="overline text-white/40 mb-1">Original</p>
          <div className="aspect-[2/3] bg-[#0E0F0C] border border-white/8 flex items-center justify-center overflow-hidden">
            {orig ? (
              <img src={orig} alt="" className="max-w-full max-h-full object-contain" data-testid={`img-original-${c.id}`} />
            ) : <span className="text-[9px] text-white/30">— stored once</span>}
          </div>
          {w && <p className="text-[9px] text-white/40 mt-1">{w}×{h}</p>}
        </div>
        {/* BBOX */}
        <div>
          <p className="overline text-white/40 mb-1">Detected bounds</p>
          <div className="aspect-[2/3] bg-[#0E0F0C] border border-white/8 flex items-center justify-center text-center p-2">
            {bw ? (
              <div>
                <p className="font-display text-2xl text-[#C17F59] tabular-nums" data-testid={`img-bbox-${c.id}`}>{bw}×{bh}</p>
                <p className="text-[9px] text-white/40 mt-1 uppercase tracking-[0.2em]">bottle pixels</p>
                {norm.bg_removed !== undefined && (
                  <p className="text-[9px] text-green-400 mt-2">bg {norm.bg_removed ? "removed" : "kept"}</p>
                )}
              </div>
            ) : <span className="text-[9px] text-white/30">no diag</span>}
          </div>
        </div>
        {/* NORMALIZED */}
        <div>
          <p className="overline text-[#C17F59] mb-1">Normalized</p>
          <div className="aspect-[2/3] bg-[#0E0F0C] border border-[#C17F59]/30 flex items-center justify-center overflow-hidden">
            <img src={final} alt="" className="max-w-full max-h-full object-contain" data-testid={`img-final-${c.id}`} />
          </div>
          {norm.bottle_fill_ratio && (
            <p className="text-[9px] text-white/40 mt-1">fill {Math.round(norm.bottle_fill_ratio * 100)}%</p>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between mb-3">
        <ConfidenceChip state={c.confidence_state} />
        <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 truncate">{c.source_domain}</p>
      </div>

      <div className="flex gap-2 flex-wrap">
        {!approved && (
          <>
            <button onClick={() => onAct(c.id, "approve")}
                    className="inline-flex items-center gap-1 bg-[#C17F59] text-[#0E0F0C] px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] hover:bg-[#D89A74]"
                    data-testid={`img-approve-${c.id}`}>
              <Check size={11} /> Approve
            </button>
            <button onClick={() => onAct(c.id, "reject")}
                    className="inline-flex items-center gap-1 border border-white/15 px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] text-white/60 hover:text-red-400 hover:border-red-400/40"
                    data-testid={`img-reject-${c.id}`}>
              <X size={11} /> Reject
            </button>
          </>
        )}
        <button onClick={() => onRenorm(c.id)}
                className="inline-flex items-center gap-1 border border-[#C17F59]/40 text-[#C17F59] px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] hover:bg-[#C17F59] hover:text-[#0E0F0C]"
                data-testid={`img-renorm-${c.id}`}>
          <Sparkles size={11} /> Re-normalize
        </button>
      </div>
    </div>
  );
}

function ConfidenceChip({ state }) {  return (
    <span className={`inline-block text-[9px] uppercase tracking-[0.22em] border px-2 py-0.5 ${CONFIDENCE_TONE[state] || "text-white/50 border-white/15"}`}
          data-testid={`conf-${state}`}>
      {state}
    </span>
  );
}

function SuggestionCard({ s, onAct }) {
  const [editing, setEditing] = useState(false);
  const [feature, setFeature] = useState(false);
  const [whyMatters, setWhyMatters] = useState("");
  const [discoText, setDiscoText] = useState("");
  const [val, setVal] = useState(
    Array.isArray(s.suggested_value) ? s.suggested_value.join(", ") :
    typeof s.suggested_value === "object" ? JSON.stringify(s.suggested_value, null, 2) :
    String(s.suggested_value || "")
  );

  const finalValue = () => {
    if (Array.isArray(s.suggested_value)) return val.split(",").map((x) => x.trim()).filter(Boolean);
    if (typeof s.suggested_value === "object") {
      try { return JSON.parse(val); } catch { return s.suggested_value; }
    }
    return val;
  };

  const isDiscoveryEligible = [
    "botanicals", "production_method", "production_water", "production_still",
    "production_notes", "brand_story", "bottle_trivia", "extended_serves", "awards",
  ].includes(s.field_type);

  const doApprove = (action) => {
    const extra = {};
    if (isDiscoveryEligible && feature) {
      extra.feature_as_discovery = true;
      if (whyMatters.trim()) extra.why_this_matters = whyMatters.trim();
      if (discoText.trim()) extra.discovery_text = discoText.trim();
    }
    onAct(s.id, action, action === "edit_approve" ? finalValue() : undefined, extra);
  };

  return (
    <div className="bg-[#101113] border border-white/8 p-4 hover:border-white/20 transition-colors" data-testid={`suggestion-${s.id}`}>
      <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
        <p className="overline text-[#C17F59]">{FIELD_LABELS[s.field_type] || s.field_type} · {s.category}</p>
        <div className="flex items-center gap-2">
          <ConfidenceChip state={s.confidence_state} />
          <a href={s.source_url} target="_blank" rel="noreferrer"
             className="text-[10px] uppercase tracking-[0.2em] text-[#D89A74] hover:underline inline-flex items-center gap-1">
            {s.source_domain} <ExternalLink size={10} />
          </a>
        </div>
      </div>

      {/* Current vs suggested */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
        <div>
          <p className="overline text-white/40 mb-1">Currently</p>
          <p className="text-xs text-white/55 italic">
            {s.current_value
              ? (Array.isArray(s.current_value) ? s.current_value.join(", ") : String(s.current_value).slice(0, 220))
              : <span className="text-white/30">— empty</span>}
          </p>
        </div>
        <div>
          <p className="overline text-[#C17F59] mb-1">Suggested</p>
          {editing ? (
            <textarea value={val} onChange={(e) => setVal(e.target.value)}
                      rows={3}
                      className="w-full bg-transparent border border-[#C17F59]/40 focus:border-[#C17F59] p-2 text-sm focus:outline-none resize-y"
                      data-testid={`edit-${s.id}`} />
          ) : (
            <p className="text-sm text-white/85" data-testid={`suggested-value-${s.id}`}>
              {Array.isArray(s.suggested_value) ? s.suggested_value.join(", ") :
               typeof s.suggested_value === "object" ? JSON.stringify(s.suggested_value) :
               String(s.suggested_value)}
            </p>
          )}
        </div>
      </div>

      {/* Source snippet */}
      {s.source_snippet && (
        <div className="border-l-2 border-[#C17F59]/30 pl-3 my-3">
          <p className="overline text-white/40 mb-1 flex items-center gap-1"><AlertCircle size={9} /> Source snippet</p>
          <p className="text-[11px] text-white/55 italic line-clamp-3">"{s.source_snippet}"</p>
        </div>
      )}

      {/* Discovery curation */}
      {isDiscoveryEligible && (
        <div className="border border-[#C17F59]/20 bg-[#0E0F0C] p-3 mb-3" data-testid={`discovery-curation-${s.id}`}>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={feature} onChange={(e) => setFeature(e.target.checked)}
                   className="accent-[#C17F59]"
                   data-testid={`feature-discovery-${s.id}`} />
            <span className="overline text-[#C17F59] flex items-center gap-1">
              <Sparkles size={11} /> Feature as Discovery on the homepage
            </span>
          </label>
          {feature && (
            <div className="mt-3 space-y-2">
              <label className="block">
                <span className="overline text-white/50 mb-1 block">Discovery line (optional — rewrite in BENO voice)</span>
                <textarea value={discoText} onChange={(e) => setDiscoText(e.target.value)} rows={2}
                          placeholder="e.g. 'Built around wild Himalayan juniper picked at extreme altitude.'"
                          className="w-full bg-transparent border border-white/15 focus:border-[#C17F59] p-2 text-xs focus:outline-none resize-y"
                          data-testid={`disco-text-${s.id}`} />
              </label>
              <label className="block">
                <span className="overline text-white/50 mb-1 block">Why this matters (optional)</span>
                <input value={whyMatters} onChange={(e) => setWhyMatters(e.target.value)}
                       placeholder="One line that bridges story to taste"
                       className="w-full bg-transparent border-b border-white/15 focus:border-[#C17F59] py-1.5 text-xs focus:outline-none"
                       data-testid={`why-matters-${s.id}`} />
              </label>
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2 flex-wrap">
        {!editing ? (
          <>
            <button onClick={() => doApprove("approve")}
                    className="inline-flex items-center gap-1.5 bg-[#C17F59] text-[#0E0F0C] px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] hover:bg-[#D89A74]"
                    data-testid={`approve-${s.id}`}>
              <Check size={11} /> {feature ? "Approve & Feature" : "Approve"}
            </button>
            <button onClick={() => setEditing(true)}
                    className="inline-flex items-center gap-1.5 border border-white/15 px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] text-white/70 hover:text-[#C17F59] hover:border-[#C17F59]"
                    data-testid={`edit-button-${s.id}`}>
              <Edit3 size={11} /> Edit first
            </button>
            <button onClick={() => onAct(s.id, "reject")}
                    className="inline-flex items-center gap-1.5 border border-white/15 px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] text-white/60 hover:text-red-400 hover:border-red-400/40"
                    data-testid={`reject-${s.id}`}>
              <X size={11} /> Reject
            </button>
          </>
        ) : (
          <>
            <button onClick={() => { doApprove("edit_approve"); setEditing(false); }}
                    className="inline-flex items-center gap-1.5 bg-[#C17F59] text-[#0E0F0C] px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] hover:bg-[#D89A74]"
                    data-testid={`save-edit-${s.id}`}>
              <Check size={11} /> Save & approve
            </button>
            <button onClick={() => setEditing(false)}
                    className="inline-flex items-center gap-1.5 border border-white/15 px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] text-white/60">
              Cancel
            </button>
          </>
        )}
      </div>
    </div>
  );
}
